import type * as React from 'react'
import { useState } from 'react'
import { PageSkeleton, skeletonLayoutForPath } from '@/components/templates/page-skeleton'
import { hasExpiredDemoSignIn } from '@/lib/demoSignInExpiry'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { ApiError, getJSON } from '@/lib/api'
import { sessionQueryOptions } from '@/lib/sessionQueryOptions'
import HealthPage from '@/pages/HealthPage'
import PlatformAssociationDetailPage from '@/pages/PlatformAssociationDetailPage'
import PlatformAssociationsListPage from '@/pages/PlatformAssociationsListPage'
import PlatformCreateAssociationPage from '@/pages/PlatformCreateAssociationPage'
import PlatformAdminSignInPage, { type PlatformSurface } from '@/pages/PlatformAdminSignInPage'
import { PaymentLinkPage } from '@/pages/PaymentLinkPage'
import { OrderLinkPage } from '@/pages/OrderLinkPage'
import { WaitlistStatusPage } from '@/pages/WaitlistStatusPage'
import { ManageLinkPage } from '@/pages/ManageLinkPage'
import { MarketingOptOutPage } from '@/pages/MarketingOptOutPage'
import PublicJoinFormPage from '@/pages/PublicJoinFormPage'
import { PublicEventPage } from '@/pages/PublicEventPage'
import { PublicEventsListingPage } from '@/pages/PublicEventsListingPage'
import StaffSignInPage, { type Association } from '@/pages/StaffSignInPage'
import MemberPortalPage from '@/pages/MemberPortalPage'
import SignInLinkUnavailablePage from '@/pages/SignInLinkUnavailablePage'
import { PublicGivePage } from '@/pages/PublicGivePage'
import { PublicCampaignPage } from '@/pages/PublicCampaignPage'
import { PublicMemberDirectoryPage } from '@/components/public-member-directory/PublicMemberDirectoryPage'
import { PublicMemberProfilePage } from '@/components/public-member-directory/PublicMemberProfilePage'
import { setAssociationDateFormat, setAssociationTimeZone } from '@/lib/dates'

export const appQueryClient = new QueryClient()

interface AssociationResponse {
  association: Association | null
}

interface PlatformSurfaceResponse {
  platform: PlatformSurface
}

export default function App() {
  return (
    <QueryClientProvider client={appQueryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<RootPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

function RootPage() {
  const associationQuery = useQuery({
    queryKey: ['association'],
    queryFn: getAssociationSurface,
    retry: false,
    ...sessionQueryOptions,
  })
  const platformSurfaceQuery = useQuery({
    queryKey: ['platform-surface'],
    queryFn: () => getJSON<PlatformSurfaceResponse>('/api/v1/platform/surface'),
    enabled: associationQuery.data?.association === null,
    retry: false,
    ...sessionQueryOptions,
  })
  const association = associationQuery.data?.association
  const platformSurface = platformSurfaceQuery.data?.platform

  setAssociationDateFormat(association?.date_format)
  setAssociationTimeZone(association?.time_zone)

  if (association) {
    const publicSurface = associationPublicSurface(association)
    if (publicSurface) return publicSurface

    return <StaffSignInPage association={association} />
  }

  if (platformSurface) {
    return <PlatformControlRoot platform={platformSurface} />
  }

  if (
    associationQuery.isLoading ||
    (associationQuery.data?.association === null && platformSurfaceQuery.isLoading)
  ) {
    return <ApplicationLoading />
  }

  return <HealthPage />
}

function ApplicationLoading() {
  return (
    <PageSkeleton layout={skeletonLayoutForPath(window.location.pathname)} label="Loading AMS..." />
  )
}

function associationPublicSurface(association: Association) {
  if (window.location.pathname === '/portal/sign-in-unavailable')
    return <SignInLinkUnavailablePage association={association} />

  if (/^\/portal(?:\/|$)/.test(window.location.pathname))
    return <MemberPortalPage association={association} />

  const orderLink = orderLinkRoute()
  if (orderLink) return <OrderLinkPage association={association} token={orderLink.token} />

  const waitlistLink = waitlistLinkRoute()
  if (waitlistLink)
    return <WaitlistStatusPage association={association} token={waitlistLink.token} />

  if (window.location.pathname === '/events')
    return <PublicEventsListingPage association={association} />

  const directorySurface = publicDirectorySurface(association)
  if (directorySurface) return directorySurface

  if (/^\/give(?:\/confirmed)?$/.test(window.location.pathname))
    return <PublicGivePage association={association} />

  const campaignSurface = publicCampaignSurface(association)
  if (campaignSurface) return campaignSurface

  const publicEvent = publicEventRoute()
  if (publicEvent)
    return <PublicEventPage association={association} eventId={publicEvent.eventId} />

  const joinRoute = publicJoinRoute()
  if (joinRoute)
    return <PublicJoinFormPage association={association} confirmation={joinRoute.confirmation} />

  const paymentLink = paymentLinkRoute()
  if (paymentLink)
    return <PaymentLinkPage token={paymentLink.token} confirmation={paymentLink.confirmation} />

  const manageLink = manageLinkRoute()
  if (manageLink) return <ManageLinkPage token={manageLink.token} />

  const marketingOptOut = marketingOptOutRoute()
  if (marketingOptOut) return <MarketingOptOutPage token={marketingOptOut.token} />
  return null
}

function publicDirectoryRoute() {
  const match = window.location.pathname.match(/^\/directory(?:\/([^/]+))?$/)
  return match ? { slug: match[1] } : null
}

function publicDirectorySurface(association: Association) {
  const publicDirectory = publicDirectoryRoute()
  if (publicDirectory?.slug)
    return <PublicMemberProfilePage association={association} slug={publicDirectory.slug} />
  if (publicDirectory) return <PublicMemberDirectoryPage association={association} />
  return null
}

function orderLinkRoute() {
  const match = window.location.pathname.match(/^\/orders\/([^/]+)$/)
  return match ? { token: match[1] } : null
}

function waitlistLinkRoute() {
  const match = window.location.pathname.match(/^\/waitlist\/([^/]+)$/)
  return match ? { token: match[1] } : null
}

function publicEventRoute() {
  const match = window.location.pathname.match(/^\/events\/(\d+)$/)
  if (!match) return null
  return { eventId: Number(match[1]) }
}

function publicCampaignRoute() {
  const match = window.location.pathname.match(/^\/campaigns\/(\d+)$/)
  return match ? { campaignId: Number(match[1]) } : null
}

function publicCampaignSurface(association: Association) {
  const publicCampaign = publicCampaignRoute()
  if (!publicCampaign) return null
  return <PublicCampaignPage association={association} campaignId={publicCampaign.campaignId} />
}

function marketingOptOutRoute() {
  const match = window.location.pathname.match(/^\/unsubscribe\/([^/]+)$/)
  return match ? { token: match[1] } : null
}

function manageLinkRoute() {
  const match = window.location.pathname.match(/^\/manage\/([^/]+)$/)
  return match ? { token: match[1] } : null
}

function publicJoinRoute() {
  if (window.location.pathname === '/join') return { confirmation: undefined }
  if (window.location.pathname !== '/join/confirmed') return null

  const params = new URLSearchParams(window.location.search)
  const paymentToken = params.get('payment_token')
  const sessionId = params.get('session_id')
  if (!paymentToken || !sessionId) return { confirmation: undefined }

  return { confirmation: { paymentToken, sessionId } }
}

function paymentLinkRoute() {
  const match = window.location.pathname.match(/^\/pay\/([^/]+)(?:\/confirmed)?$/)
  if (!match) return null

  return {
    token: match[1],
    confirmation: window.location.pathname.endsWith('/confirmed'),
  }
}

async function getAssociationSurface(): Promise<AssociationResponse> {
  try {
    return await getJSON<AssociationResponse>('/api/v1/association')
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { association: null }
    throw error
  }
}

function PlatformControlRoot({ platform }: { platform: PlatformSurface }) {
  const [expiredDemoLink, setExpiredDemoLink] = useState(hasExpiredDemoSignIn)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const signInFallback = (
    <PlatformAdminSignInPage
      platform={platform}
      onSignedIn={() => {
        setExpiredDemoLink(false)
        queryClient.invalidateQueries({ queryKey: ['platform-associations'] })
        queryClient.invalidateQueries({ queryKey: ['platform-association'] })
      }}
    />
  )

  if (expiredDemoLink) return signInFallback

  return (
    <Routes>
      <Route
        path="/platform/associations/new"
        element={
          <PlatformCreateAssociationPage
            platform={platform}
            unauthorizedFallback={signInFallback}
            onCancel={() => navigate('/platform/associations')}
            onCreated={(association) => navigate(`/platform/associations/${association.id}`)}
          />
        }
      />
      <Route
        path="/platform/associations/:associationId"
        element={
          <PlatformAssociationDetailRoute
            platform={platform}
            unauthorizedFallback={signInFallback}
          />
        }
      />
      <Route
        path="*"
        element={
          <PlatformAssociationsListPage
            platform={platform}
            unauthorizedFallback={signInFallback}
            onCreateAssociation={() => navigate('/platform/associations/new')}
          />
        }
      />
    </Routes>
  )
}

function PlatformAssociationDetailRoute({
  platform,
  unauthorizedFallback,
}: {
  platform: PlatformSurface
  unauthorizedFallback: React.ReactNode
}) {
  const { associationId } = useParams()
  const parsedAssociationId = Number(associationId)

  if (!Number.isInteger(parsedAssociationId) || parsedAssociationId <= 0) {
    return <HealthPage />
  }

  return (
    <PlatformAssociationDetailPage
      platform={platform}
      associationId={parsedAssociationId}
      unauthorizedFallback={unauthorizedFallback}
    />
  )
}
